if (!window.customElements.get('popup-dialog')) {
  class PopupDialog extends HTMLElement {
    constructor() {
      super();
      this.focusableElements = [];
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.scrollPosition = 0;
      this._scrollLocked = false;
      this._scrollLockGeneration = 0;
      this.close = this.close.bind(this);
      this.open = this.open.bind(this);
      this._dialogListenersBound = false;
    }

    keepInPlace() {
      return this.hasAttribute('data-popup-keep-in-place');
    }

    _resolveDialog() {
      if (this.dialog?.isConnected) return this.dialog;

      const wrapInHost = this.querySelector('.popup__wrap');
      if (wrapInHost?.isConnected) return wrapInHost;

      if (!this.id) return null;

      const wrapById = document.getElementById(this.id + '-wrap');
      if (wrapById?.isConnected) return wrapById;

      const controlsId = this.querySelector('button.popup__trigger')?.getAttribute('aria-controls');
      if (controlsId) {
        const wrapByControls = document.getElementById(controlsId);
        if (wrapByControls?.isConnected) return wrapByControls;
      }

      return null;
    }

    _bindDialogListeners() {
      if (this._dialogListenersBound || !this.dialog) return;
      this._dialogListenersBound = true;

      if (this.trigger) {
        this.trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      }
      this.closeButton?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      if (this.dialogId && document.querySelector(`[data-a11y-dialog-hide="${this.dialogId}"]`)) {
        document.querySelectorAll(`[data-a11y-dialog-hide="${this.dialogId}"]`).forEach((btn) => {
          btn.addEventListener('click', () => this.close());
        });
      }
    }
  
    reInit() {
      this.dialog = this._resolveDialog();
      if (!this.dialog) return false;

      this.trigger = this.querySelector('button.popup__trigger');
      this.dialogId = this.id;
      this.closeButton = this.dialog.querySelector('.popup__close');
      this.overlay = this.dialog.querySelector('.popup__overlay');
      this.modal = this.dialog.querySelector('.popup');

      if (this.trigger) {
        this.trigger.setAttribute('aria-haspopup', 'dialog');
        this.trigger.setAttribute('aria-controls', this.dialog.id);
      }

      this.dialog.setAttribute('role', 'dialog');
      this.dialog.setAttribute('aria-modal', 'true');
      this.dialog.setAttribute('aria-hidden', 'true');
      this.closeButton?.setAttribute('aria-label', 'Close dialog');

      if (this.keepInPlace()) {
        this._bindDialogListeners();
      } else if (this.dialog.parentElement !== document.body) {
        document.body.appendChild(this.dialog);
        this._bindDialogListeners();
      } else if (!this._dialogListenersBound) {
        this._bindDialogListeners();
      }
      return true;
    }

    connectedCallback() {
      this.reInit();
    }

    _lockScroll() {
      if (this._scrollLocked) return;
      this._scrollLockGeneration += 1;
      const generation = this._scrollLockGeneration;
      const apply = () => {
        if (generation !== this._scrollLockGeneration || this._scrollLocked) return;
        if (!this.dialog?.classList.contains("active")) return;
        const lock = window.sharedScrollLock;
        if (!lock) return;
        this.markScrollables(lock);
        lock.disablePageScroll();
        this._scrollLocked = true;
      };
      if (window.sharedScrollLock) apply();
      else if (typeof whenReady === "function") whenReady("sharedScrollLock", apply);
    }

    markScrollables(lock = window.sharedScrollLock) {
      if (!lock || !this.dialog) return;
      this.dialog.querySelectorAll("[data-scroll-lock-scrollable]").forEach((el) => {
        lock.markScrollable(el);
      });
    }

    _unlockScroll() {
      this._scrollLockGeneration += 1;
      if (!this._scrollLocked) return;
      window.sharedScrollLock?.enablePageScroll();
      this._scrollLocked = false;
    }

    disconnectedCallback() {
      this._unlockScroll();
      if (!this.keepInPlace() && this.dialog?.parentElement === document.body) {
        this.dialog.remove();
      }
      this.dialog = null;
      this.closeButton = null;
      this.overlay = null;
      this.modal = null;
      this._dialogListenersBound = false;
    }

    open() {
      if (!this.reInit() || !this.dialog?.isConnected) return false;
      if (this.dialog.classList.contains('active')) return true;

      this.dialog.classList.add('active');
      this.overlay?.classList.add('active');
      if (this.modal) {
        setTimeout(() => {
          this.modal?.classList.add('active');
        }, 150);
      }
      this.dialog.setAttribute('aria-hidden', 'false');
      this.focusableElements = Array.from(
        this.dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      );
      if (this.closeButton) {
        this.closeButton.focus();
      }

      this._lockScroll();
  
      document.addEventListener('keydown', this.handleKeyDown);

      this.dispatchEvent(new CustomEvent('show'));
      return true;
    }
  
    close() {

      if(!this.dialog) return;

      const wasOpen = this.dialog.classList.contains('active') || this._scrollLocked;
      this.dialog.classList.remove('active');
      this.overlay?.classList.remove('active');
      this.modal?.classList.remove('active');
      this.dialog.setAttribute('aria-hidden', 'true');
      if(this.trigger){
        this.trigger.focus();
      }
  
      document.removeEventListener('keydown', this.handleKeyDown);
  
      setTimeout(() => {
        document.querySelector("html").style.scrollBehavior = "auto";
      }, 10);    
    
      if (wasOpen) this._unlockScroll();
      
      this.dispatchEvent(new CustomEvent('hide'));
  
    }
  
    handleKeyDown(event) {
      if (event.key === 'Escape') {
        this.close();
      } else if (event.key === 'Tab') {
        const first = this.focusableElements[0];
        const last = this.focusableElements[this.focusableElements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  }
  
  customElements.define('popup-dialog', PopupDialog);
}
